import { doc, getDoc } from "firebase/firestore";
import { realEstateListingsCollection } from "./collections";
import type { ListingContact } from "../../@types/sharedSchema";

/**
 * THE RECORDED OWNER, READ FROM THE AUTHORITY.
 *
 * "Who is the seller of this listing" is answered by `resolveListingSeller`, and
 * on a `webot` row the answer lives in `listingContacts` — the seller/landlord
 * the realtor typed into her own listing. This module is where that field comes
 * from.
 *
 * ## Why not from the mirror row the surface already holds
 *
 * Because `yad2-listings-latest` MUST NOT CARRY IT. That collection is
 * `allow get: if true` in `firestore.rules` — unauthenticated — since the
 * share-link favourites doc hands anonymous buyers the ids of listings a realtor
 * chose to share, and the public client page point-reads those documents by id.
 * Mirroring the owner would publish his private mobile to the very buyers she is
 * showing the property to, and they could then call him and cut her out. The
 * mirror Cloud Function and `convertListingToProcessedListingData` both carry a
 * loud note about this; so does the field's declaration in `sharedSchema.ts`.
 *
 * `real-estate-listings` is the authority, it is not world-readable by that
 * unauthenticated `get` rule's reasoning (it is a listing the caller owns or
 * shares an org with), and — the bonus — IT IS NOT STALE. The mirror trails a
 * save by up to about a minute (listing → mirror CF → `listings-sync-queue` → a
 * once-a-minute drain), so reading the authority means the owner she typed thirty
 * seconds ago is on screen NOW instead of after the sync catches up. A field that
 * had been mirrored would have been the slower answer as well as the leaky one.
 *
 * ## The shape of the read, and why it is this shape
 *
 * A board page renders up to 200 `React.memo`'d rows and re-renders them on
 * scroll, sort and filter. So:
 *
 *  - **Webot rows only.** Every other origin is a market ad with no
 *    `real-estate-listings` document at all; asking for one is a guaranteed miss.
 *    Callers gate on `listingOrigin === 'webot'`, which is a small minority of a
 *    typical board page.
 *  - **Batched.** Ids requested in the same tick are coalesced into ONE flush
 *    (`BATCH_WINDOW_MS`), so a page that paints twelve webot rows issues one
 *    burst of reads rather than twelve independent waterfalls.
 *  - **Cached per id**, including the negative answer — most webot listings
 *    record no owner, and without caching that the board would re-read them on
 *    every remount.
 *  - **No subscription.** Deliberately NOT `onSnapshot`: 200 rows cannot each
 *    hold a listener, and this value changes only when the realtor edits the
 *    listing herself, which `invalidateRecordedListingContacts` covers exactly.
 *  - **Lazy.** Nothing is read until a row asks; there is no prefetch pass over
 *    the page.
 *
 * The point-read + retry shape is transcribed from `fetchListings.ts` (which
 * targets the mirror) so the two behave the same way on a flaky network.
 */

/** Resolved entries for one listing. `[]` is a real answer: nothing recorded. */
type CacheEntry = {
  contacts: ListingContact[];
  fetchedAt: number;
};

/**
 * How long a resolved answer is reused.
 *
 * Short on purpose. The value changes when the realtor edits the listing's
 * seller, and while every edit path we own calls
 * `invalidateRecordedListingContacts`, a board left open beside a second tab (or
 * a colleague's edit) has no such signal. A minute bounds how long a stale owner
 * can sit on screen while still collapsing the read traffic of scrolling a long
 * board.
 */
const CACHE_TTL_MS = 60_000;

/** Ids asked for within this window go out together. One tick is enough. */
const BATCH_WINDOW_MS = 30;

/**
 * How long a FAILED id is left alone before anyone may ask again.
 *
 * ⚠ Load-bearing, not politeness. Every flush notifies every subscriber so they
 * can pick up their answer; a subscriber whose id has no cached answer asks
 * again, which schedules another flush, which notifies again. With a failing
 * read (offline, a rules denial on a listing the caller cannot see) that is an
 * unbounded loop of Firestore reads. This clock is what breaks it, and it is
 * deliberately short enough that a blip costs one stale row for ten seconds.
 */
const FAILURE_BACKOFF_MS = 10_000;

/**
 * Reads per flush. The client SDK has no multi-get, so a batch is N parallel
 * point reads; this keeps a big page from opening a hundred sockets at once.
 */
const MAX_PARALLEL_READS = 20;

const cache = new Map<string, CacheEntry>();
/** In-flight reads, so two rows asking for the same id share one round trip. */
const inFlight = new Map<string, Promise<ListingContact[] | undefined>>();
/** id → epoch ms before which a failed id must not be re-requested. */
const backoffUntil = new Map<string, number>();
const pending = new Set<string>();
const listeners = new Set<() => void>();

let flushTimer: ReturnType<typeof setTimeout> | null = null;
let flushWaiters: Array<() => void> = [];

const notify = (): void => {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.error("recordedListingContacts: listener threw", error);
    }
  });
};

/**
 * Transcribed from `fetchListings.withRetries` — a transient blip (network,
 * a 503 from the Firestore frontend, an auth refresh) must not be the reason a
 * listing reports "no owner recorded" for someone who has one.
 */
async function withRetries<T>(attempts: number, baseDelayMs: number, fn: () => Promise<T>): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt === attempts - 1) break;
      const delay = baseDelayMs * Math.pow(2, attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  throw lastError;
}

/** A recorded entry counts only once it names someone or carries a number. */
const hasSubstance = (contact?: ListingContact | null): boolean =>
  Boolean(
    contact?.callerFirstName?.trim() || contact?.callerLastName?.trim() || contact?.callerPhoneNumber?.trim()
  );

/**
 * Blank rows are dropped here rather than by the resolver's caller.
 *
 * The listing form keeps an empty seller row around while the realtor types (and
 * after she clears one), so a document legitimately holds
 * `[{ '', '', '' }]` — which is "no owner", not "an owner with no details".
 * `resolveListingSeller` applies the same test, so this is belt and braces; it
 * also keeps the cached array honest for anything that reads `.length`.
 */
const readListingContacts = async (listingId: string): Promise<ListingContact[]> => {
  const snapshot = await withRetries(3, 100, () => getDoc(doc(realEstateListingsCollection, listingId)));
  if (!snapshot.exists()) return [];
  return (snapshot.data()?.listingContacts ?? []).filter(hasSubstance);
};

const isFresh = (entry: CacheEntry | undefined): entry is CacheEntry =>
  Boolean(entry) && Date.now() - (entry as CacheEntry).fetchedAt < CACHE_TTL_MS;

async function flush(): Promise<void> {
  flushTimer = null;
  const ids = [...pending];
  pending.clear();
  const waiters = flushWaiters;
  flushWaiters = [];

  for (let i = 0; i < ids.length; i += MAX_PARALLEL_READS) {
    const chunk = ids.slice(i, i + MAX_PARALLEL_READS);
    // eslint-disable-next-line no-await-in-loop
    await Promise.all(
      chunk.map(async (id) => {
        try {
          const contacts = await readListingContacts(id);
          cache.set(id, { contacts, fetchedAt: Date.now() });
          backoffUntil.delete(id);
        } catch (error) {
          // NOT cached as `[]`. "The read failed" and "no owner is recorded" are
          // different answers, and only the second may be shown — reporting a
          // failure as "no owner" is how a real person gets mislabelled by a
          // network blip. Instead we remember nothing and refuse to be asked
          // again for `FAILURE_BACKOFF_MS`, which is what stops the retry loop.
          backoffUntil.set(id, Date.now() + FAILURE_BACKOFF_MS);
          console.error(`recordedListingContacts: gave up on ${id} after retries`, error);
        } finally {
          inFlight.delete(id);
        }
      })
    );
  }

  waiters.forEach((resolve) => resolve());
  notify();
}

const scheduleFlush = (): Promise<void> => {
  const waited = new Promise<void>((resolve) => {
    flushWaiters.push(resolve);
  });
  if (!flushTimer) flushTimer = setTimeout(() => void flush(), BATCH_WINDOW_MS);
  return waited;
};

/**
 * The cached answer, or `undefined` when we do not know yet.
 *
 * ⚠ `undefined` is NOT "no owner" — `[]` is. A caller that conflates them shows
 * the listing AGENT as the seller for the duration of every read.
 */
export function getRecordedListingContacts(listingId: string): ListingContact[] | undefined {
  const entry = cache.get(listingId);
  return isFresh(entry) ? entry.contacts : undefined;
}

/**
 * Ask for one listing's recorded owner, joining whatever batch is forming.
 *
 * Resolves with the entries — `[]` when none are recorded, `undefined` when we
 * still do not know (the read failed, or it is inside its failure backoff). A
 * caller must render `undefined` as "unknown", never as "no owner": see the
 * failure branch in `flush`.
 */
export function loadRecordedListingContacts(
  listingId: string
): Promise<ListingContact[] | undefined> {
  const cached = getRecordedListingContacts(listingId);
  if (cached) return Promise.resolve(cached);

  const existing = inFlight.get(listingId);
  if (existing) return existing;

  // Inside the backoff window we answer "unknown" WITHOUT scheduling a flush.
  // Enqueuing here is exactly the loop the backoff exists to break, because
  // every flush notifies every subscriber and each one re-asks.
  const blockedUntil = backoffUntil.get(listingId);
  if (blockedUntil && Date.now() < blockedUntil) return Promise.resolve(undefined);

  pending.add(listingId);
  const promise = scheduleFlush().then(() => getRecordedListingContacts(listingId));
  inFlight.set(listingId, promise);
  return promise;
}

/**
 * Forget what we know, so the next reader re-reads.
 *
 * Call it from anything that CHANGES a listing's recorded seller — the badge's
 * save (which stamps `contactId` onto the entry), the listing form's save. Omit
 * the id to drop everything, which is what a sign-out or an account switch wants.
 */
export function invalidateRecordedListingContacts(listingId?: string): void {
  if (listingId) {
    cache.delete(listingId);
    // An explicit invalidation is a deliberate act by someone who just wrote,
    // so it also clears any failure backoff — she should not wait it out.
    backoffUntil.delete(listingId);
  } else {
    cache.clear();
    backoffUntil.clear();
  }
  notify();
}

/** Notified after every flush and every invalidation. Used by the hook. */
export function subscribeToRecordedListingContacts(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
